import { Box, Button, ButtonGroup, Grid, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import Switch from 'components/switch';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { usePolicyInfoContext } from '../contexts/policyInfoContext';

import { FormPolicy } from '../services/types';
import { DropDown } from 'components/dropdown';

const useStyles = makeStyles((theme) => ({
  removeSwitchContainer: {
    marginTop: theme.spacing(1),
    marginBottom: -40,
  },
  title: {
    color: "black",
    fontSize: '0.9rem',
    fontWeight: "bold"
  },
  button: { textTransform: 'capitalize' },
  container: {
    display: "flex", alignItems: "center", justifyContent: "space-between"
  },
  disclosureContainer: {
    display: "flex", alignItems: "center", gap: "10px"
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    width: '40rem',
    // The parent <Paper> (in policy-info-refactor.tsx) is the actual flex item that shrinks
    // at down('md') — this Box just needs to fill whatever width that Paper ends up with.
    [theme.breakpoints.down('lg')]: {
      width: '100%',
    },
  },
  fieldRow: {
    display: 'flex',
    gap: '1rem',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  fieldHalf: {
    width: '50%',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
}));

const PolicyTypeForm = () => {
  const { t } = useTranslation('policyInfoAndRates');
  const { control, getValues, setValue, watch } = useFormContext<FormPolicy>();
  const classes = useStyles();

  const { options, selectedTab } = usePolicyInfoContext();
  const selectedPolicy: "owners_policy_tab" | "lenders_policy_tab" = selectedTab?.id!;

  const { policy_rate_types, policy_stats, policy_types } = options?.[selectedPolicy] || {};

  const disclosureLendersSection = watch(`${selectedPolicy}.disclosure_section`);

  return (
    <Box className={classes.root}>
      <Box>
        <Typography className={classes.title}>{t('policyTypeLabel')}</Typography>
        <Controller
          control={control}
          name={`${selectedPolicy}.policy_type`}
          render={({ field: { ref, ...field } }) => (
            <DropDown
              defaultText={t('common:select-one')}
              options={policy_types || []}
              optionText="description"
              optionValue="code"
              onChange={field.onChange}
              value={getValues(`${selectedPolicy}.policy_type`) || ''}
            />
          )}
        />
      </Box>

      <Box className={classes.fieldRow}>
        <Box className={classes.fieldHalf}>
          <Typography className={classes.title}>{t('rateTypeLabel')}</Typography>
          <Controller
            control={control}
            name={`${selectedPolicy}.rate_type`}
            render={({ field: { ref, ...field } }) => (
              <DropDown
                defaultText={t('common:select-one')}
                options={policy_rate_types || []}
                optionText="description"
                optionValue="code"
                onChange={(value) => {
                  const { onChange } = field;
                  onChange(value);
                  const otherpolicy = selectedPolicy === "lenders_policy_tab"
                    ? 'owners_policy_tab'
                    : 'lenders_policy_tab';
                  const policyValue = getValues(
                    `${otherpolicy}.rate_type`
                  );
                  if (policyValue === '') {
                    setValue(
                      `${otherpolicy}.rate_type`,
                      value
                    );
                  }
                }}
                value={getValues(`${selectedPolicy}.rate_type`) || ''}
              />
            )}
          />
        </Box>

        <Box className={classes.fieldHalf}>
          <Typography className={classes.title}>{t('rateTypeStatCodeLabel')}</Typography>
          <Controller
            control={control}
            name={`${selectedPolicy}.stat_code`}
            render={({ field: { ref, ...field } }) => (
              <DropDown
                defaultText={t('common:select-one')}
                options={policy_stats || []}
                optionText="description"
                optionValue="code"
                onChange={field.onChange}
                value={getValues(`${selectedPolicy}.stat_code`) || ''}
              />
            )}
          />
        </Box>
      </Box>

      {selectedPolicy === "lenders_policy_tab" && (
        <Box className={classes.container}>
          <Box className={classes.disclosureContainer}>
            <ButtonGroup disableElevation color="primary" size="small">
              <Button
                className={classes.button}
                variant={disclosureLendersSection === 'B' ? 'contained' : 'outlined'}
                onClick={() => { setValue(`${selectedPolicy!}.disclosure_section`, 'B', { shouldDirty: true }); }}
              >
                B
              </Button>

              <Button
                className={classes.button}
                variant={disclosureLendersSection === 'C' ? 'contained' : 'outlined'}
                onClick={() => { setValue(`${selectedPolicy}.disclosure_section`, 'C', { shouldDirty: true }); }}
              >
                C
              </Button>
            </ButtonGroup>
            <Typography>{t('disclosureSectionLabel')}</Typography>
          </Box>
          <Controller
            control={control}
            name={`${selectedPolicy}.remove_section_13`}
            render={({ field: { ref, ...field } }) => (
              <Switch
                {...field}
                inputRef={ref}
                checked={
                  getValues(`${selectedPolicy}.remove_section_13`) || false
                }
                label={t('policyInfoAndRates:remove-section-13')}
                handleBlur={field.onBlur}
                handleOnChange={field.onChange}
              />
            )}
          />
        </Box>
      )}
    </Box>
  );
};

export default PolicyTypeForm;
